import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import {
  type DeviceViewSettingsTable,
  type NodeGraphExtra,
  type NodeSn,
  type NodeType,
  type ProtocolEdgeType,
  isCloudNetworkNodeType,
  isNullable,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import {
  DEVICE_ATTRIBUTE_DETAIL_TAB,
  DEVICE_DETAIL_TAB,
  INTENT_CHECK_DETAIL_TAB,
  PATH_INSPECTOR_DETAIL_TAB,
  RESOURCE_HIERARCHY_DETAIL_TAB,
  STP_DETAIL_TAB,
} from 'components/DeviceView/deviceViewConstants';
import type {
  DeviceViewElementType,
  DeviceViewEntityId,
  DeviceViewLabelsTab,
  TabData,
} from 'components/DeviceView/deviceViewTypes';
import ScrollingTabs from 'components/ScrollingTabs';
import { useLocale } from 'containers/App/LocaleContext';
import FileSaver from 'file-saver';
import { type Draft, produce } from 'immer';
import { type FC, type MouseEvent, memo, useState } from 'react';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';
import { isCloudConstructNode } from '../helpers/cloudConstruct';
import { hasNodeCustomTableSettings } from '../helpers/customNodeTableSettings';
import {
  getDeviceViewTabOrder,
  getTabsIndex,
} from '../helpers/getDeviceViewTabOrder';
import { hasNodeDetailTab } from '../helpers/hasNodeDetailTab';
import { isDeviceDetailData } from '../helpers/isDeviceDetailData';
import { useDeviceViewContextSelectors } from '../model/DeviceViewModel';
import {
  DeviceViewContentCliLogButton,
  isDownloadAvailable,
} from './DeviceViewContentCliLogButton';
import {
  DeviceViewContentConfigurationButton,
  isConfigurationAvailable,
} from './DeviceViewContentConfigurationButton';
import {
  DeviceViewContentConnectButton,
  isConnectAvailable,
} from './DeviceViewContentConnectButton';
import { DeviceViewContentDownloadJsonButton } from './DeviceViewContentDownloadJsonButton';
import { DeviceViewContentTabTable } from './DeviceViewContentTabTable';

interface ConfigurationDownloading {
  [sn: string]: boolean;
}

interface Props {
  deviceViewElement: DeviceViewElementType;
  deviceViewEntityId: DeviceViewEntityId;
  deviceViewEntityTables: DeviceViewSettingsTable[];
  extra: NodeGraphExtra | null;
  nodeType: NodeType | null;
  intentCheckIsApplied: boolean;
  isPathLookupNode: boolean;
  nodeHasPathInspectorDetail: boolean;
  onShowSetupTables: (e: MouseEvent) => void;
  parentNodeSn?: NodeSn | null;
  protocol?: ProtocolEdgeType;
  selectedTable: string | null;
  labelsTabs: DeviceViewLabelsTab[];
}

const lastChangeAtToIsoDateString = (lastChangeAt: number | null) =>
  new Date(Number(lastChangeAt)).toISOString();

const isDataAvailable = (tabData: TabData): boolean =>
  !!tabData &&
  (isConfigurationAvailable(tabData.configuration) ||
    (isDeviceDetailData(tabData.device) &&
      (isConnectAvailable(tabData.device) ||
        isDownloadAvailable(tabData.device))));

const renderLabelsTabs = (props: Props) =>
  props.labelsTabs.map((tab) => (
    <DeviceViewContentTabTable
      active={props.selectedTable === tab.columnKey}
      deviceViewEntityId={props.deviceViewEntityId}
      key={tab.columnKey}
      label={tab.title}
      tableId={tab.columnKey}
    />
  ));

const renderLabelsEntityTabs = (props: Props) =>
  props.deviceViewElement === 'labels' ? renderLabelsTabs(props) : null;

const BareDeviceViewContentDetailHeader: FC<Props> = (props) => {
  const ctxProps = useDeviceViewContextSelectors({
    settings: prop('settings'),
    tabData: prop('tabData'),
  });
  const msg = useLocale();
  const [header, setHeader] = useState<HTMLDivElement | null>(null);
  const apiRequest = useApi().request;
  const [configurationDownloading, setConfigurationDownloading] =
    useState<ConfigurationDownloading>({});
  const { showNotification } = useNotifications();
  const tabIds = getDeviceViewTabOrder({
    deviceViewElement: props.deviceViewElement,
    deviceViewEntityTables: props.deviceViewEntityTables,
    extra: props.extra,
    intentCheckIsApplied: props.intentCheckIsApplied,
    isPathLookupNode: props.isPathLookupNode,
    labelsTabs: props.labelsTabs,
    nodeHasPathInspectorDetail: props.nodeHasPathInspectorDetail,
    nodeType: props.nodeType,
    protocol: props.protocol,
  });
  const handleDownloadConfiguration = useStableCallback(async () => {
    try {
      setConfigurationDownloading(
        produce((draft: Draft<ConfigurationDownloading>) => {
          draft[props.deviceViewEntityId] = true;
        }),
      );
      const file = await apiRequest({
        // Fixed noUncheckedIndexedAccess NIM-19408
        url: `/tables/management/configuration/download?hash=${ctxProps.tabData[props.deviceViewEntityId]!.configuration!.hash}&sn=${ctxProps.tabData[props.deviceViewEntityId]!.configuration!.sn}&sanitized=false`,
        method: 'GET',
      });
      const blob = new Blob([file], { type: 'text/plain;charset=utf-8' });
      // Fixed noUncheckedIndexedAccess NIM-19408
      const cfg = ctxProps.tabData[props.deviceViewEntityId]!.configuration!;
      // !! DO NOT REUSE
      // Downloads should be handled by providing proper headers from backend and initiated using `<a href=""`
      // to use browser's native progress indication and avoid issues with large files.
      FileSaver.saveAs(
        // !! DO NOT REUSE
        blob,
        `${cfg.hostname}-${lastChangeAtToIsoDateString(cfg.lastChangeAt)}.txt`,
      );
      setConfigurationDownloading(
        produce((draft: Draft<ConfigurationDownloading>) => {
          draft[props.deviceViewEntityId] = false;
        }),
      );
    } catch {
      showNotification({
        type: 'warning',
        message: msg.deviceView.cantGetConfigFile.replace(
          '{hostname}',
          // Fixed noUncheckedIndexedAccess NIM-19408
          ctxProps.tabData[props.deviceViewEntityId]!.configuration!.hostname!,
        ),
      });
    } finally {
      setConfigurationDownloading(
        produce((draft: Draft<ConfigurationDownloading>) => {
          draft[props.deviceViewEntityId] = false;
        }),
      );
    }
  });

  return (
    <div
      className={classNames(
        'device-view-collapsible-bottom-bar__select-table',
        'flex',
        'flex-row',
        'items-center',
        'bg-white',
        'relative',
        'pr-lgu-3',
        'pl-lgu-3',
      )}
      ref={(el: HTMLDivElement) => setHeader(el)}
    >
      <div className='scrolling-tabs flex-1'>
        <div className='nav-tabs-custom mb-0'>
          {header && (
            <ScrollingTabs
              container={header}
              endIndex={getTabsIndex(tabIds, props.selectedTable)}
              itemsCount={tabIds.length}
            >
              {props.intentCheckIsApplied && (
                <DeviceViewContentTabTable
                  active={props.selectedTable === INTENT_CHECK_DETAIL_TAB}
                  deviceViewEntityId={props.deviceViewEntityId}
                  key={INTENT_CHECK_DETAIL_TAB}
                  label={msg.deviceView.intentCheckDetailTab}
                  tableId={INTENT_CHECK_DETAIL_TAB}
                />
              )}
              {props.deviceViewElement === 'node' && (
                <>
                  {props.isPathLookupNode &&
                    props.nodeHasPathInspectorDetail && (
                      <DeviceViewContentTabTable
                        active={
                          props.selectedTable === PATH_INSPECTOR_DETAIL_TAB
                        }
                        deviceViewEntityId={props.deviceViewEntityId}
                        key={PATH_INSPECTOR_DETAIL_TAB}
                        label={msg.deviceView.devicePathLookupTab}
                        tableId={PATH_INSPECTOR_DETAIL_TAB}
                      />
                    )}
                  {hasNodeDetailTab(props.nodeType) && (
                    <DeviceViewContentTabTable
                      active={props.selectedTable === DEVICE_DETAIL_TAB}
                      deviceViewEntityId={props.deviceViewEntityId}
                      key={DEVICE_DETAIL_TAB}
                      label={msg.deviceView.hostnameTab}
                      tableId={DEVICE_DETAIL_TAB}
                    />
                  )}
                  {/* NIM-23705: cloud constructs additionally expose their
                      resource hierarchy in a dedicated tab next to Detail. */}
                  {isCloudConstructNode(props.nodeType, props.extra) && (
                    <DeviceViewContentTabTable
                      active={
                        props.selectedTable === RESOURCE_HIERARCHY_DETAIL_TAB
                      }
                      deviceViewEntityId={props.deviceViewEntityId}
                      key={RESOURCE_HIERARCHY_DETAIL_TAB}
                      label={msg.deviceView.resourceHierarchyTab}
                      tableId={RESOURCE_HIERARCHY_DETAIL_TAB}
                    />
                  )}
                  {renderLabelsTabs(props)}
                  {/* NIM-24243: cloud constructs (Cloud Network / NIC /
                      Subnet / Cloud Network Peering) are not vDevice rows
                      and have no device attributes, so the on-prem
                      Attributes tab does not apply to them. Cloud Network
                      nodes are the exception while ENABLE_NEW_CLOUD_MODEL is
                      off (NIM-24373): they fall back to the old device
                      tab in that case. */}
                  {(!isCloudConstructNode(props.nodeType, props.extra) ||
                    (props.nodeType !== null &&
                      isCloudNetworkNodeType(props.nodeType) &&
                      !isFeatureFlagEnabled('ENABLE_NEW_CLOUD_MODEL'))) && (
                    <DeviceViewContentTabTable
                      active={
                        props.selectedTable === DEVICE_ATTRIBUTE_DETAIL_TAB
                      }
                      deviceViewEntityId={props.deviceViewEntityId}
                      key={DEVICE_ATTRIBUTE_DETAIL_TAB}
                      label={msg.deviceView.deviceAttributesTab}
                      tableId={DEVICE_ATTRIBUTE_DETAIL_TAB}
                    />
                  )}
                </>
              )}
              {renderLabelsEntityTabs(props)}
              {props.protocol === 'stp' && (
                <DeviceViewContentTabTable
                  active={props.selectedTable === STP_DETAIL_TAB}
                  deviceViewEntityId={props.deviceViewEntityId}
                  key={STP_DETAIL_TAB}
                  label={msg.deviceView.stpDetailTab}
                  tableId={STP_DETAIL_TAB}
                />
              )}
              {props.deviceViewEntityTables.map(
                (table: DeviceViewSettingsTable) => (
                  <DeviceViewContentTabTable
                    active={props.selectedTable === table.webEndpoint}
                    deviceViewEntityId={props.deviceViewEntityId}
                    key={table.webEndpoint}
                    label={table.label}
                    tableId={table.webEndpoint}
                  />
                ),
              )}
            </ScrollingTabs>
          )}
        </div>
      </div>
      {ctxProps.settings.nodes &&
        props.deviceViewElement === 'node' &&
        props.nodeType !== null &&
        (!hasNodeCustomTableSettings(props.nodeType) ||
          (hasNodeCustomTableSettings(props.nodeType) &&
            // Check parentNodeSn if selected node types should have custom table settings
            isNullable(props.parentNodeSn))) && (
          <>
            <Tooltip content={msg.deviceView.setupTablesTooltip}>
              <Button
                variant='tertiary'
                onClick={props.onShowSetupTables}
                icon='fa-pen'
                type='button'
              />
            </Tooltip>
            {props.nodeType &&
              isDataAvailable(ctxProps.tabData[props.deviceViewEntityId]!) && (
                <span className='device-view-collapsible-bottom-bar__header-separator ml-2' />
              )}
          </>
        )}
      {props.nodeType && (
        <>
          <DeviceViewContentConnectButton
            tabData={ctxProps.tabData[props.deviceViewEntityId]!}
          />
          <DeviceViewContentCliLogButton
            tabData={ctxProps.tabData[props.deviceViewEntityId]!}
          />
          <DeviceViewContentConfigurationButton
            configurationDownloading={
              configurationDownloading[props.deviceViewEntityId]!
            }
            onDownloadConfiguration={handleDownloadConfiguration}
            tabData={ctxProps.tabData[props.deviceViewEntityId]!}
          />
          <DeviceViewContentDownloadJsonButton
            tabData={ctxProps.tabData[props.deviceViewEntityId]}
          />
        </>
      )}
    </div>
  );
};

export const DeviceViewContentDetailHeader = memo(
  BareDeviceViewContentDetailHeader,
);
